// CEP lookup capability facade (VER-389). The vendor (BrasilAPI) appears in this
// file only. BrasilAPI v2 over ViaCEP: it fans out across Correios/ViaCEP/WideNet
// (a superset of any one source's availability) and answers an honest 404 for an
// unknown CEP, where ViaCEP answers HTTP 200 with {"erro": true}.
//
// This is a cross-origin fetch deliberately NOT routed through @/lib/api — that
// wrapper prefixes API_URL and attaches the Clerk token, neither of which belongs
// on a public third-party request.

export type CepLookup =
  | { status: 'ok'; logradouro: string; bairro: string; cidade: string; uf: string }
  | { status: 'not_found' }
  | { status: 'unavailable' };

const LOOKUP_TIMEOUT_MS = 4_000;

/**
 * Never throws: an autofill must never be able to break the form it fills.
 * Abort (caller-triggered or timeout) and any network/shape failure read as
 * 'unavailable'; only a definite 404 reads as 'not_found'.
 */
export async function lookupCep(cep: string, signal?: AbortSignal): Promise<CepLookup> {
  const digits = cep.replace(/\D/g, '');
  if (digits.length !== 8) return { status: 'not_found' };

  try {
    const response = await fetch(`https://brasilapi.com.br/api/cep/v2/${digits}`, {
      signal: signal
        ? AbortSignal.any([signal, AbortSignal.timeout(LOOKUP_TIMEOUT_MS)])
        : AbortSignal.timeout(LOOKUP_TIMEOUT_MS),
    });
    if (response.status === 404) return { status: 'not_found' };
    if (!response.ok) return { status: 'unavailable' };

    const body = (await response.json()) as {
      street?: string; neighborhood?: string; city?: string; state?: string;
    };
    return {
      status: 'ok',
      logradouro: body.street ?? '',
      bairro: body.neighborhood ?? '',
      cidade: body.city ?? '',
      uf: body.state ?? '',
    };
  } catch {
    return { status: 'unavailable' };
  }
}
